import { useState } from 'react'
import { Video, Mail, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react'
import { auth, ApiError } from '../../lib/api'

type PageState = 'idle' | 'loading' | 'success' | 'error'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [state, setState] = useState<PageState>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return

    setState('loading')
    setErrorMsg('')

    try {
      await auth.requestMagicLink(email.trim())
      setState('success')
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : 'Something went wrong. Please try again.'
      setErrorMsg(message)
      setState('error')
    }
  }

  return (
    <div className="min-h-screen bg-alk-white flex items-center justify-center p-4">
      {/* Background accent */}
      <div className="absolute top-0 left-0 w-1 h-full bg-alk-red" />

      <div className="w-full max-w-sm">
        {/* Logo mark */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-alk-black flex items-center justify-center">
            <Video size={20} className="text-alk-red" />
          </div>
          <div>
            <div className="font-display font-bold text-xl uppercase tracking-widest text-alk-black leading-none">
              VideoLab
            </div>
            <div className="text-xs text-alk-gray uppercase tracking-widest">
              Alchatek
            </div>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white border border-gray-200 p-6">
          {state === 'success' ? (
            <div className="text-center py-4">
              <CheckCircle className="mx-auto mb-3 text-green-600" size={36} />
              <h2 className="font-display text-xl uppercase tracking-wide text-alk-black mb-2">
                Check Your Email
              </h2>
              <p className="text-sm text-alk-gray">
                We sent a magic link to{' '}
                <span className="font-semibold text-alk-black">{email}</span>.
                Click the link to sign in.
              </p>
              <button
                onClick={() => { setState('idle'); setEmail('') }}
                className="mt-5 text-xs text-alk-red font-display uppercase tracking-wide hover:underline"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <>
              <h2 className="font-display text-2xl uppercase tracking-wide text-alk-black mb-1">
                Sign In
              </h2>
              <p className="text-sm text-alk-gray mb-6">
                Enter your email to receive a magic link.
              </p>

              <form onSubmit={handleSubmit} noValidate>
                <label
                  htmlFor="email"
                  className="section-label block mb-1.5"
                >
                  Email Address
                </label>
                <div className="flex gap-0">
                  <div className="flex items-center px-3 bg-gray-100 border border-r-0 border-gray-300">
                    <Mail size={15} className="text-alk-gray" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@alchatek.com"
                    autoComplete="email"
                    required
                    className="input-field border-l-0 flex-1"
                    disabled={state === 'loading'}
                  />
                </div>

                {state === 'error' && (
                  <div className="flex items-center gap-2 mt-3 p-2.5 bg-red-50 border border-red-200">
                    <AlertCircle size={15} className="text-alk-red flex-shrink-0" />
                    <span className="text-xs text-alk-red">{errorMsg}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={state === 'loading' || !email.trim()}
                  className="btn-primary w-full mt-4 flex items-center justify-center gap-2"
                >
                  {state === 'loading' ? (
                    <>
                      <span
                        className="w-4 h-4 border-2 border-white/30 border-t-white animate-spin"
                        aria-hidden
                      />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Magic Link
                      <ArrowRight size={15} />
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-xs text-alk-gray text-center mt-4">
          Access restricted to authorized Alchatek personnel.
        </p>
      </div>
    </div>
  )
}
